import { useAlert } from "@components/alert/withAlert";
import BusyIndicator from "@components/busyIndicator";
import { Button, ButtonGroup } from "@components/button";
import { DownloadIcon } from "@components/icon";
import { SmartTable } from "@components/smart/smartTable";
import SectionSwitch from "@components/splitWizard/SectionSwitch";
import { createBindingContext } from "@odata/BindingContext";
import { ODataError } from "@odata/Data.types";
import {
    EntitySetName,
    IPrPayrollPeriodEntity,
    IPrPayslipEntity,
    PrPayrollPeriodEntity,
    PrPayslipEntity,
    PrYearOperationEntity
} from "@odata/GeneratedEntityTypes";
import { PrPayrollPeriodStatusCode } from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { transformToODataString } from "@odata/OData.utils";
import { parseResponse } from "@odata/ODataParser";
import { TableWrapper } from "@pages/fiscalYearClose/FiscalYearClose.styles";
import {
    GENERATE_PAYROLL_SHEETS,
    ISectionProps
} from "@pages/payroll/payrollYearOperations/PayrollYearOperation.utils";
import {
    downloadPayrollSheet,
    getPayrollSheetsProcessingTableDef
} from "@pages/payroll/payrollYearOperations/screens/PayrollSheetsProcessing.utils";
import {
    AlertWrapper,
    ClickableStyled,
    Text
} from "@pages/payroll/payrollYearOperations/screens/PayrollYearOperationScreen.styles";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import { logger } from "@utils/log";
import React, { useCallback, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { IconSize, Status, TextAlign, ValueType } from "../../../../enums";
import DateType, { DisplayFormat } from "../../../../types/Date";
import { getAlertFromError } from "../../../../views/formView/Form.utils";

async function getEmployeesWithPayedPayslip(oData: OData, year: string): Promise<number[]> {
    const periodsRes = await oData.getEntitySetWrapper(EntitySetName.PrPayrollPeriods).query()
        .filter(`${PrPayrollPeriodEntity.StatusCode} eq '${PrPayrollPeriodStatusCode.ProcessedAndClosed}' AND ${PrPayrollPeriodEntity.Year} eq ${year}`)
        .orderBy(PrPayrollPeriodEntity.Month, false)
        .top(1)
        .fetchData<IPrPayrollPeriodEntity[]>();

    const lastPaidMonth = periodsRes.value[0]?.Month;

    if (!lastPaidMonth) {
        return [];
    }

    const payslipsRes = await oData.getEntitySetWrapper(EntitySetName.PrPayslips).query()
        .filter(`${PrPayslipEntity.Year} eq ${year} AND ${PrPayslipEntity.Month} le ${lastPaidMonth}`)
        .groupBy(`${PrPayslipEntity.Employee}/Id`)
        .fetchData<IPrPayslipEntity[]>();

    return payslipsRes.value?.map(ps => ps.Employee?.Id);
}

const PayrollSheetsProcessing: React.FC<ISectionProps> = ({
                                                              task,
                                                              onCheck,
                                                              storage,
                                                              year
                                                          }) => {
    const { t } = useTranslation(["PayrollYearOperation", "Employee"]);
    const { alert, setAlert } = useAlert({});
    const [isBusy, setBusy] = React.useState<boolean>(false);
    const [employeeIds, setEmployeeIds] = React.useState<number[]>([]);

    useEffect((): void => {
        setBusy(true);
        getEmployeesWithPayedPayslip(storage.oData, year).then((ids) => {
            setEmployeeIds(ids);
            if (!ids?.length) {
                setAlert({
                    status: Status.Warning,
                    title: t("PayrollYearOperation:Alerts.NoProcessableSheets"),
                });
            }
        }).finally(() => setBusy(false));
    }, [year, storage.oData, setAlert, t]);

    const archivePayrollSheets = useCallback(async () => {
        try {
            const response = await customFetch(GENERATE_PAYROLL_SHEETS.replace("{year}", year), getDefaultPostParams());
            if (!response.ok) {
                const error: ODataError = await parseResponse(response);
                setAlert(getAlertFromError(error));
            }
        } catch (e) {
            setAlert(getAlertFromError(e));
            logger.error("Error while generating payroll sheets", e);
        }
    }, [year, setAlert]);

    const cancelArchivation = useCallback(async () => {
        setBusy(true);
        try {
            const res = await storage.save({
                skipETag: true,
                skipLoad: true,
                skipBusy: true,
                data: {
                    [PrYearOperationEntity.PrYearOperationPayrollSheets]: null
                }
            });

            if (!!res) {
                setAlert({
                    status: Status.Success,
                    title: t("PayrollYearOperation:Alerts.ArchivationCancelled"),
                });
                await storage.reload();
            } else {
                setAlert({
                    status: Status.Error,
                    title: t("Common:Errors.ErrorHappened")
                });
            }
        } catch (e) {
            setAlert(getAlertFromError(e));
            logger.error("Error while removing payroll sheets", e);
        }
        setBusy(false);
    }, [setAlert, t, storage]);

    const handleDownloadAllSheets = useCallback(async () => {
        await downloadPayrollSheet(employeeIds, year, storage.context);
    }, [year, employeeIds, storage.context]);

    const handleCheck = useCallback(async (checked: boolean) => {
        const alertProps = await onCheck(checked);
        setAlert(alertProps);
    }, [onCheck, setAlert]);

    const tableDef = getPayrollSheetsProcessingTableDef(storage.context, year);
    const bindingContext = createBindingContext(EntitySetName.PrEmployees, storage.oData.metadata);
    const filterQuery = !!employeeIds?.length ? {
        query: `Id in (${transformToODataString(employeeIds, ValueType.Number)})`
    } : null;

    const hasProcessedSheets = storage.getValueByPath(PrYearOperationEntity.PrYearOperationPayrollSheets)?.length > 0;

    const date = storage.getValueByPath(PrYearOperationEntity.PrYearOperationPayrollSheets)?.[0]?.DateCreated;

    return (<>
        {isBusy && <BusyIndicator/>}
        <AlertWrapper>{alert}</AlertWrapper>
            {!!date &&
                <Text><Trans i18nKey={"PayrollYearOperation:PayrollSheetsProcessing.ArchivationDate"}
                             values={{ date: DateType.format(date, DisplayFormat.DateShort) }}
                             components={{
                                 1: <strong/>
                             }}/>
                </Text>}
        {!!employeeIds?.length && <>
            <TableWrapper>
                <SmartTable bindingContext={bindingContext}
                            initialSortBy={tableDef.initialSortBy}
                            columns={tableDef.columns}
                            filter={filterQuery}
                            tableId={tableDef.id}
                            disableVirtualization
                            preventAutoReset={tableDef.preventAutoReset}
                            hideDrilldown
                            drilldown={null}/>
            </TableWrapper>
            <ClickableStyled
                onClick={handleDownloadAllSheets}>
                {t("PayrollYearOperation:PayrollSheetsProcessing.DownLoadAllSheets")}
                <DownloadIcon width={IconSize.XS} height={IconSize.XS}/>
            </ClickableStyled>
            <ButtonGroup align={TextAlign.Right}>
                <Button
                    isTransparent
                    isDisabled={!hasProcessedSheets}
                    onClick={cancelArchivation}>{t("PayrollYearOperation:PayrollSheetsProcessing.CancelArchivation")}</Button>
                <Button
                    isDisabled={hasProcessedSheets}
                    onClick={archivePayrollSheets}>{t("PayrollYearOperation:PayrollSheetsProcessing.ArchivePayrollSheets")}</Button>
            </ButtonGroup>
        </>}
        <SectionSwitch
                isChecked={task.IsChecked}
                isDisabled={!hasProcessedSheets}
                onCheck={handleCheck}/>
    </>);
};

export default PayrollSheetsProcessing;